/**
 * Small apps, mods and widgets shown in the Apps section: public GitHub repos,
 * plus a few web destinations (an external product and two in-site pages).
 *
 * Static on purpose: the section must not depend on the GitHub API at runtime
 * (rate limits, private repos, an outage would blank the tile). Adding an app is
 * one entry here plus its `apps.items.<id>.*` copy in all three locale files.
 * Only public repositories belong here as GitHub apps — the link is the whole point of a tile.
 */

export type AppPlatform = "macos" | "claudeCode" | "mcp" | "watch" | "web";

/** Lifecycle shown in the detail sheet; only `shipped` takes the accent. */
export type AppStatus = "shipped" | "beta" | "prototype" | "wip";

export type AppId = "snapclip" | "todoList" | "collapseTools" | "agentRemote" | "agentsOffice" | "omen" | "blog" | "tromso" | "loopBreaker";

type AppBase = {
    /** Also the i18n key under `apps.items`. */
    id: AppId;
    /** Product names are not translated. */
    name: string;
    platforms: AppPlatform[];
    status: AppStatus;
    stack: string[];
    /** Square app icon under /public; apps without one get the drawn pixel agent. */
    icon?: string;
    /** Set when the artwork is a light glyph that needs a dark tile to show in both themes. */
    iconTone?: "dark";
    /** Wide screenshot shown in the detail sheet, taken from the repo's README. */
    preview?: string;
    /** Year first published, shown as a mono meta. */
    year: number;
};

/** A public GitHub repo: "Get" links to it and the sheet lists its install commands. */
type GithubApp = AppBase & {
    repoUrl: string;
    /** Commands from the repo's README, in order, shown verbatim in the detail sheet. */
    install: string[];
    webUrl?: never;
};

/**
 * A web destination: "Open" goes to `webUrl`. A value starting with "/" is an in-site
 * route (locale-prefixed at render, same tab); anything else is an external https URL
 * (new tab). Web apps have no install block.
 */
type WebApp = AppBase & {
    webUrl: string;
    repoUrl?: never;
    install?: never;
};

export type AppEntry = GithubApp | WebApp;

/** In-site routes the Apps section may link to; each must exist in `src/App.tsx`. */
export const IN_SITE_APP_ROUTES: ReadonlyArray<string> = ["/blog", "/beyond-code", "/loop-breaker"];

export function isInSiteRoute(url: string): boolean {
    return url.startsWith("/");
}

export const APPS: ReadonlyArray<AppEntry> = [
    {
        id: "snapclip",
        name: "SnapClip",
        platforms: ["macos"],
        status: "shipped",
        stack: ["Swift", "AppKit", "Homebrew"],
        repoUrl: "https://github.com/0xnicholasy/snapclip",
        icon: "/apps/snapclip-icon.webp",
        preview: "/apps/snapclip-preview.webp",
        install: [
            "brew install 0xnicholasy/tap/snapclip",
            'ln -sf "$(brew --prefix)/opt/snapclip/SnapClip.app" /Applications/SnapClip.app',
            "open /Applications/SnapClip.app",
        ],
        year: 2026,
    },
    {
        id: "todoList",
        name: "Todo List",
        platforms: ["claudeCode"],
        status: "shipped",
        stack: ["TypeScript", "Claude Code plugin", "MCP"],
        repoUrl: "https://github.com/0xnicholasy/claude-mod-todo-list",
        icon: "/apps/todo-list-icon.webp",
        preview: "/apps/todo-list-preview.webp",
        install: [
            "/plugin marketplace add 0xnicholasy/claude-mod-todo-list",
            "/plugin install todo-list@claude-mod-todo-list",
        ],
        year: 2026,
    },
    {
        id: "collapseTools",
        name: "Claude Fold",
        platforms: ["claudeCode"],
        status: "shipped",
        stack: ["TypeScript", "Claude Code plugin"],
        repoUrl: "https://github.com/0xnicholasy/claude-mod-collapse-tools",
        icon: "/apps/collapse-tools-icon.webp",
        install: [
            "/plugin marketplace add 0xnicholasy/claude-mods",
            "/plugin install collapse-tools@claude-mods",
        ],
        year: 2026,
    },
    {
        id: "agentRemote",
        name: "Agent Remote",
        platforms: ["watch", "macos"],
        status: "prototype",
        stack: ["TypeScript", "Swift", "watchOS"],
        repoUrl: "https://github.com/0xnicholasy/agent-remote",
        icon: "/apps/agent-remote-icon.webp",
        install: [
            "git clone https://github.com/0xnicholasy/agent-remote.git",
            "cd agent-remote && bun install",
            "bun run dev",
        ],
        year: 2026,
    },
    {
        id: "agentsOffice",
        name: "Agents Office",
        platforms: ["claudeCode"],
        status: "wip",
        stack: ["TypeScript", "Claude Code mod"],
        repoUrl: "https://github.com/0xnicholasy/claude-mod-agents-rpg",
        install: [
            "git clone https://github.com/0xnicholasy/claude-mod-agents-rpg.git",
            "cd claude-mod-agents-rpg && claude",
            "/office",
        ],
        year: 2026,
    },
    {
        id: "omen",
        name: "OMEN",
        platforms: ["web"],
        status: "shipped",
        stack: ["React", "NestJS", "PostgreSQL", "Solidity", "Base"],
        webUrl: "https://www.omen.energy/",
        icon: "/omen-mark.png",
        iconTone: "dark",
        preview: "/omen-preview.webp",
        year: 2025,
    },
    {
        id: "blog",
        name: "Blog",
        platforms: ["web"],
        status: "shipped",
        stack: ["React", "TypeScript", "Markdown"],
        webUrl: "/blog",
        icon: "/apps/blog-icon.webp",
        year: 2026,
    },
    {
        id: "tromso",
        name: "Tromsø After Dark",
        platforms: ["web"],
        status: "shipped",
        stack: ["three.js", "WebGL 2", "JavaScript"],
        webUrl: "/beyond-code",
        icon: "/apps/tromso-icon.webp",
        year: 2026,
    },
    {
        id: "loopBreaker",
        name: "Loop Breaker",
        platforms: ["web"],
        status: "shipped",
        stack: ["YouTube", "Remotion", "ElevenLabs"],
        webUrl: "/loop-breaker",
        icon: "/apps/loop-breaker-icon.webp",
        year: 2026,
    },
];

/** Platforms that at least one app ships on, in first-seen order, so the filter never offers an empty view. */
export const APP_PLATFORMS: ReadonlyArray<AppPlatform> = [...new Set(APPS.flatMap((app) => app.platforms))];

export function filterApps(apps: ReadonlyArray<AppEntry>, platform: AppPlatform | "all"): AppEntry[] {
    return platform === "all" ? [...apps] : apps.filter((app) => app.platforms.includes(platform));
}
